// src/components/BuildingViews/ForgeView.js
import React, { useState } from 'react';
import styles from './ForgeView.module.css';
import { ItemData } from '../../data/ItemData';
import { getItemImageSrc } from '../../utils/getItemImageSrc';

const ForgeView = ({ playerData, onPlayerAction, onGoBack }) => {
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get current crafting status
  const activeCrafting = playerData?.buildings?.forge?.activeCrafting;

  const forgeRecipes = [
    {
      id: 'craftCopperPickaxe',
      name: 'Forge Copper Pickaxe',
      description: 'Craft a durable copper pickaxe for efficient mining',
      input: { 'copperBar': 3, 'plank': 1 },
      output: { 'copperPickaxe': 1 },
      duration: 180, // 3 minutes
      icon: 'wearables/copperPickaxe.png'
    },
    {
      id: 'craftCopperAxe',
      name: 'Forge Copper Axe',
      description: 'Craft a sharp copper axe for faster wood cutting',
      input: { 'copperBar': 2, 'plank': 2 },
      output: { 'copperAxe': 1 },
      duration: 150, // 2.5 minutes
      icon: 'wearables/copperAxe.png'
    },
    {
      id: 'craftCopperSword',
      name: 'Forge Copper Sword',
      description: 'Craft a reliable copper sword for combat',
      input: { 'copperBar': 4, 'plank': 1 },
      output: { 'copperSword': 1 },
      duration: 240, // 4 minutes
      icon: 'wearables/copperSword.png'
    },
    {
      id: 'craftWoodenShield',
      name: 'Craft Wooden Shield',
      description: 'Basic shield for protection',
      input: { 'plank': 3, 'string': 1 },
      output: { 'woodenShield': 1 },
      duration: 120, // 2 minutes
      icon: 'wearables/woodenShield.png'
    },
    {
      id: 'craftCopperShield',
      name: 'Forge Copper Shield',
      description: 'Improved shield with copper reinforcement',
      input: { 'copperBar': 2, 'plank': 2, 'string': 1 },
      output: { 'copperShield': 1 },
      duration: 180, // 3 minutes
      icon: 'wearables/copperShield.png'
    },
    {
      id: 'craftIronShield',
      name: 'Forge Iron Shield',
      description: 'Strong iron shield for heavy defense',
      input: { 'ironBar': 3, 'plank': 2, 'string': 1 },
      output: { 'ironShield': 1 },
      duration: 240, // 4 minutes
      icon: 'wearables/ironShield.png'
    },
    {
      id: 'craftCopperRing',
      name: 'Forge Copper Ring',
      description: 'Simple copper ring',
      input: { 'copperBar': 1 },
      output: { 'copperRing': 1 },
      duration: 60, // 1 minute
      icon: 'wearables/copperRing.png'
    },
    {
      id: 'craftGoldRing',
      name: 'Forge Gold Ring',
      description: 'Valuable gold ring',
      input: { 'goldBar': 1 },
      output: { 'goldRing': 1 },
      duration: 90, // 1.5 minutes
      icon: 'wearables/goldRing.png'
    },
    {
      id: 'craftCrudeSteelSword',
      name: 'Forge Crude Steel Sword',
      description: 'Rough but effective steel weapon',
      input: { 'ironBar': 3, 'copperBar': 1, 'plank': 1 },
      output: { 'crudeSteelSword': 1 },
      duration: 300, // 5 minutes
      icon: 'wearables/crudeSteelSword.png'
    }
  ];

  const canCraftRecipe = (recipe) => {
    for (const [itemKey, requiredAmount] of Object.entries(recipe.input)) {
      const available = playerData.inventory[itemKey] || 0;
      if (available < requiredAmount) {
        return false;
      }
    }
    return true;
  };

  const handleCraftClick = (recipe) => {
    if (!canCraftRecipe(recipe) || activeCrafting) {
      return;
    }

    setIsProcessing(true);
    setSelectedRecipe(recipe);
    
    // Call the action through the parent component
    onPlayerAction(recipe.id);
    
    // Reset processing state after a brief delay
    setTimeout(() => {
      setIsProcessing(false);
      setSelectedRecipe(null);
    }, 1000);
  };

  const handleStopCrafting = () => {
    onPlayerAction('stopCrafting', { buildingKey: 'forge' });
  };

  const handleToggleContinuous = () => {
    onPlayerAction('toggleContinuousCrafting', { buildingKey: 'forge' });
  };

  const buildingLevel = playerData.buildings?.forge?.upgradeLevel || 0;
  const efficiencyBonus = buildingLevel * 15; // 15% time reduction per level

  return (
    <div className={styles.forgeContainer}>
      {onGoBack && (
        <button 
          className={styles.backButton}
          onClick={onGoBack}
          title="Back to Home"
        >
          ✕
        </button>
      )}
      <div className={styles.forgeHeader}>
        <img src="/img/buildingItems/forge.png" alt="Forge" className={styles.forgeIcon} />
        <div className={styles.forgeInfo}>
          <h2>Forge Operations</h2>
          <p>Level {buildingLevel} {efficiencyBonus > 0 && `(-${efficiencyBonus}% time)`}</p>
          <p className={styles.description}>
            Advanced metalworking station for quality tools and weapons
          </p>
        </div>
      </div>

      {/* Active Crafting Progress */}
      {activeCrafting && (
        <div className={styles.craftingProgress}>
          <h3>🔨 Currently Forging</h3>
          <div className={styles.activeRecipe}>
            <div className={styles.recipeInfo}>
              <span className={styles.recipeName}>
                {activeCrafting.actionConfig.output ? 
                  Object.keys(activeCrafting.actionConfig.output).map(key => 
                    ItemData[key]?.name || key
                  ).join(', ') :
                  ItemData[activeCrafting.actionConfig.outputItemKey]?.name || activeCrafting.actionConfig.outputItemKey
                }
              </span>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${(activeCrafting.progress || 0) * 100}%` }}
                ></div>
              </div>
              <span className={styles.progressText}>
                {Math.floor((activeCrafting.progress || 0) * 100)}% Complete
              </span>
            </div>
            <div className={styles.craftingControls}>
              <button 
                onClick={handleToggleContinuous}
                className={`${styles.controlButton} ${activeCrafting.continuousCrafting ? styles.active : ''}`}
              >
                {activeCrafting.continuousCrafting ? '🔄 Continuous ON' : '🔄 Continuous OFF'}
              </button>
              <button 
                onClick={handleStopCrafting}
                className={`${styles.controlButton} ${styles.stopButton}`}
              >
                ⏹️ Stop
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.recipeGrid}>
        {forgeRecipes.map((recipe) => {
          const canCraft = canCraftRecipe(recipe);
          const isSelected = selectedRecipe?.id === recipe.id;
          
          return (
            <div 
              key={recipe.id} 
              className={`${styles.recipeCard} ${!canCraft ? styles.disabled : ''} ${isSelected ? styles.selected : ''}`}
            >
              <div className={styles.recipeHeader}>
                <img 
                  src={getItemImageSrc(recipe.icon)} 
                  alt={recipe.name} 
                  className={styles.recipeIcon} 
                />
                <div className={styles.recipeInfo}>
                  <h3>{recipe.name}</h3>
                  <p>{recipe.description}</p>
                </div>
              </div>

              <div className={styles.recipeDetails}>
                <div className={styles.requirements}>
                  <h4>Required:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.input).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      const available = playerData.inventory[itemKey] || 0;
                      const hasEnough = available >= amount;
                      
                      return (
                        <div key={itemKey} className={`${styles.itemRequirement} ${!hasEnough ? styles.insufficient : ''}`}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>
                            {available}/{amount}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.output}>
                  <h4>Produces:</h4>
                  <div className={styles.itemList}>
                    {Object.entries(recipe.output).map(([itemKey, amount]) => {
                      const item = ItemData[itemKey];
                      
                      return (
                        <div key={itemKey} className={styles.itemOutput}>
                          <img 
                            src={getItemImageSrc(itemKey)} 
                            alt={item?.name || itemKey} 
                            className={styles.itemIcon} 
                          />
                          <span>{item?.name || itemKey}</span>
                          <span className={styles.itemAmount}>x{amount}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.recipeStats}>
                  <div className={styles.duration}>
                    <span>⏱️ {Math.floor(recipe.duration * (1 - efficiencyBonus / 100))}s</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => handleCraftClick(recipe)}
                disabled={!canCraft || isProcessing || activeCrafting}
                className={`${styles.craftButton} ${isProcessing && isSelected ? styles.processing : ''}`}
              >
                {activeCrafting ? 'Forge Busy' : (isProcessing && isSelected ? 'Forging...' : 'Start Forging')}
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.forgeTips}>
        <h4>🔥 Forge Tips</h4>
        <ul>
          <li>Forge requires refined materials like copper bars</li>
          <li>Higher level forge creates items faster</li>
          <li>Copper tools last much longer than wooden ones</li>
          <li>Always keep backup tools in case of breakage</li>
        </ul>
      </div>
    </div>
  );
};

export default ForgeView; 